import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import Chart from "chart.js/auto";
import { classifyAppError, toError } from '@dash/randy/shared/errors/error-policy';
import { CLIENT_REQUEST_ID_HEADER, httpSpanRegistry } from '@dash/randy/shared/observability/http-span-registry';
import { OWNED_API_URL_PATTERNS } from '@dash/randy/shared/observability/owned-api';
import { parameterizePath } from '@dash/randy/shared/observability/route-pattern';
import { PAGE_LOAD_TRACE_TIMEOUT_MS } from '@dash/randy/shared/observability/page-load-tracing.service';
import { resolveTraceSampleRate } from '@dash/randy/shared/observability/trace-sampling';

console.log('localize:', typeof $localize);

if (environment.production) {
  enableProdMode();
}

let lastDialogShownAt = 0;

const REPORT_DIALOG_COOLDOWN_MS = 10 * 60 * 1000;

const sentryClient = Sentry.init({
  dsn: 'https://8147a8042e28f19dfc0abc2b118d8271@o4506468623974400.ingest.us.sentry.io/4506469023481856',
  enabled: environment.sentry.enabled,
  release: environment.sentry.release,
  dist: $localize?.locale,
  sendDefaultPii: false,
  sampleRate: 1.0,
  tracePropagationTargets: OWNED_API_URL_PATTERNS,
  integrations: [
    Sentry.browserTracingIntegration({
      childSpanTimeout: PAGE_LOAD_TRACE_TIMEOUT_MS + 5_000,
      enableLongAnimationFrame: true,
      enableHTTPTimings: true,
      finalTimeout: PAGE_LOAD_TRACE_TIMEOUT_MS + 5_000,
      beforeStartSpan(options) {
        const fallbackName = parameterizeTransactionName(options.name);
        return {
          ...options,
          name: fallbackName,
          attributes: {
            ...options.attributes,
            ...getNavigationAttributes(options.op),
          },
        };
      },
      onRequestSpanStart(span, requestInformation) {
        httpSpanRegistry.registerAutomaticSpan(
          requestInformation.headers?.get(CLIENT_REQUEST_ID_HEADER) ?? null,
          span,
        );
      },
      onRequestSpanEnd(span, responseInformation) {
        httpSpanRegistry.enrichAutomaticSpan(span, responseInformation.headers);
      },
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.feedbackIntegration({
      autoInject: false,
      showBranding: false,
      enableScreenshot: false,
      isEmailRequired: false,
      isNameRequired: false,
      useSentryUser: {
        email: 'email',
        name: 'username',
      },
    }),
  ],
  debug: environment.sentry.debug,
  environment: environment.sentry.environment,
  profileSessionSampleRate: environment.sentry.profileSessionSampleRate,
  profileLifecycle: 'trace',
  beforeSend(event, hint) {
    const isExceptionEvent = !!event.exception;
    const originalException = hint?.originalException;
    const classification = isExceptionEvent && originalException ? classifyAppError(originalException) : null;
    const eventIsExpected = isExceptionEvent && (classification?.kind === 'expected' || event.tags?.error_kind === 'expected');

    if (classification) {
      event.tags = {
        ...event.tags,
        error_kind: event.tags?.error_kind ?? classification.kind,
        error_reason: event.tags?.error_reason ?? classification.reason,
      };
    }

    if (eventIsExpected) {
      return null;
    }

    if (!environment.production && environment.sentry.debug) {
      console.error(event, 'Unexpected application error');
    }

    return event;
  },

  tracesSampler: samplingContext =>
    samplingContext.inheritOrSampleWith(
      resolveTraceSampleRate(
        environment.sentry.traceSampleRate,
        environment.sentry.pageTraceSampleRate,
        samplingContext.location?.pathname,
        samplingContext.name,
      ),
    ),
});

sentryClient?.on('afterSendEvent', event => {
  if (!environment.production || !event.exception || !event.event_id) {
    return;
  }

  const now = Date.now();
  if (now - lastDialogShownAt <= REPORT_DIALOG_COOLDOWN_MS) {
    return;
  }

  lastDialogShownAt = now;
  Sentry.showReportDialog({
    eventId: event.event_id,
    lang: $localize`nl`,
    title: $localize`Er is een fout opgetreden`,
    subtitle: $localize`Het team is op de hoogte gesteld.`,
    subtitle2: $localize`Help ons door een melding te maken.`,
    labelComments: $localize`Wat is er gebeurd?`,
    labelClose: $localize`Sluiten`,
    labelSubmit: $localize`Verstuur`,
    labelName: $localize`Naam`,
    labelEmail: $localize`E-mail`,
    labelSuccess: $localize`Melding verzonden`,
    successMessage: $localize`Bedankt voor het melden van de fout`,
    errorFormEntry: $localize`Niet alle velden zijn ingevuld`,
  });

  if (environment.sentry.debug) {
    const exception = event.exception.values?.[0];
    console.log(
      'Showed Sentry dialog for',
      `${exception?.type ?? 'unknown'}:${exception?.value ?? event.message ?? 'unknown'}`,
    );
  }
});

Chart.defaults.color = '#878f9e';


platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch(async err => {
    console.log('Bootstrap error');
    console.error(err);
    Sentry.captureException(toError(err), {
      tags: {
        error_kind: 'unexpected',
        error_reason: 'angular-bootstrap',
        error_source: 'angular-bootstrap',
      },
    });
    await Sentry.flush(2_000);
  });

function parameterizeTransactionName(name: string): string {
  try {
    const url = new URL(name, window.location.origin);
    return parameterizePath(url.pathname);
  } catch {
    return parameterizePath(name);
  }
}

function getNavigationAttributes(operation: string | undefined): Record<string, string> {
  if (operation === 'navigation') {
    return { 'dashboard.navigation.kind': 'internal_navigation' };
  }

  if (operation !== 'pageload') {
    return {};
  }

  const browserType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type
    ?? 'unknown';
  let navigationKind = 'page_load';
  if (browserType === 'reload') {
    navigationKind = 'page_reload';
  } else if (browserType === 'back_forward') {
    navigationKind = 'browser_history';
  }

  return {
    'dashboard.navigation.browser_type': browserType,
    'dashboard.navigation.kind': navigationKind,
  };
}
